const COLS = [["min", "MIN"], ["pts", "PTS"], ["reb", "REB"], ["ast", "AST"], ["fg3m", "3PM"], ["pra", "PRA"]];
const f1 = (v) => (v == null ? "–" : v.toFixed(1));

function Cell({ b, a, testId }) {
  const d = (a ?? 0) - (b ?? 0);
  const tone = Math.abs(d) < 0.05 ? "text-slate-500" : d > 0 ? "text-emerald-300" : "text-rose-300";
  return (
    <td className="px-2 py-2 text-center font-mono-num whitespace-nowrap" data-testid={testId}>
      <span className="text-slate-400">{f1(b)}</span>
      <span className="text-slate-600 mx-1">→</span>
      <span className="text-slate-50 font-bold">{a == null ? "OUT" : f1(a)}</span>
      <span className={`block text-[10px] ${tone}`}>{Math.abs(d) < 0.05 ? "±0" : `${d > 0 ? "+" : ""}${d.toFixed(1)}`}</span>
    </td>
  );
}

function Tag({ children, className }) {
  return <span className={`ml-1 text-[10px] font-bold uppercase rounded px-1 ${className}`}>{children}</span>;
}

export function ComparisonTable({ ctx, result }) {
  const { before, after } = result;
  const rows = result.rows.filter((r) => r.before || r.after);
  const missing = result.rows.filter((r) => r.no_history);
  return (
    <div data-testid="rotation-comparison">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-800">
        <h3 className="font-display text-xl font-bold uppercase tracking-wide mr-auto">Before / After · {ctx.team_code} vs {ctx.opp_code}</h3>
        <span data-testid="rotation-minutes-check" className={`font-mono-num text-sm px-2 py-1 rounded border ${after.partial ? "border-amber-500/60 text-amber-300" : "border-emerald-500/50 text-emerald-300"}`}>
          {after.allocated_minutes.toFixed(1)} / 240 MIN{after.partial ? ` · partial (${after.remaining_minutes.toFixed(1)} unallocated)` : ""}
        </span>
        <span data-testid="rotation-redistribution" className="text-xs text-slate-400">{result.redistribution ? "Redistribution active" : "No redistribution"} · max {after.max_minutes.toFixed(1)} MIN</span>
      </div>
      {Object.keys(after.capped || {}).some((k) => after.capped[k].uncapped - after.totals[k] > 0.5) && (
        <p data-testid="rotation-ceiling-note" className="px-4 py-2 text-xs text-amber-300 border-b border-slate-800">
          Team ceiling applied (last season's best team average): {Object.entries(after.capped).filter(([k, v]) => v.uncapped - after.totals[k] > 0.5)
            .map(([k, v]) => `${k.toUpperCase()} ${v.uncapped.toFixed(1)} → ${after.totals[k].toFixed(1)}`).join(" · ")}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
              <th className="text-center pl-4 pr-1 py-2 w-14">#</th>
              <th className="text-left px-3 py-2">Player</th>
              {COLS.map(([k, l]) => <th key={k} className="text-center px-2 py-2">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.player_id} data-testid={`rotation-row-${r.player_id}`} className={`border-b border-slate-800/70 ${r.out ? "bg-rose-500/5" : r.replacement ? "bg-blue-500/10" : ""}`}>
                <td className="pl-4 pr-1 py-2 text-center"><strong className="ra-number" data-testid={`rotation-jersey-${r.player_id}`}>{r.jersey ?? "–"}</strong></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="ra-player-cell">
                  <span className="ra-avatar">{r.photo && <img src={r.photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</span>
                  <div className="min-w-0">
                  <span className="font-semibold text-slate-100">{r.name}</span>
                  <span className={`ra-role ${r.starter || (!ctx.starters?.length && r.role === "starter") ? "starter" : "bench"}`} data-testid={`rotation-role-${r.player_id}`}>{r.starter ? "Starter" : !ctx.starters?.length && r.role === "starter" ? "Proj. starter" : "Bench"}</span>
                  {r.out && <Tag className="text-rose-300 bg-rose-500/15">OUT</Tag>}
                  {r.replacement && <Tag className="text-blue-200 bg-blue-500/20">Fixed</Tag>}
                  {r.smoothed && <Tag className="text-amber-300 bg-amber-500/10" >{{ bench_position: "bench pos. rate", bench_team: "bench team rate", team: "team rate" }[r.rate_source] || "own rate"}</Tag>}
                  <span className="block text-[11px] text-slate-400 mt-0.5" data-testid={`rotation-blend-${r.player_id}`}
                    title={r.prior_share != null ? `Rate blend: ${Math.round(r.prior_share * 100)}% ${r.prior_source?.startsWith("rookie") ? "bench peers" : "last season"} / ${Math.round((1 - r.prior_share) * 100)}% this season` : "No rate"}>
                    {r.position || "–"} · {r.role || "–"} · {r.sample_minutes} season MIN
                  </span>
                  </div>
                  </div>
                </td>
                {COLS.map(([k]) => <Cell key={k} b={r.before?.[k]} a={r.after?.[k]} testId={`rotation-cell-${k}-${r.player_id}`} />)}
              </tr>
            ))}
            <tr className="bg-blue-600/10 border-t-2 border-blue-500/60" data-testid="rotation-team-total">
              <td />
              <td className="px-3 py-3 font-display text-lg font-extrabold uppercase tracking-wide">Team total <span className="block text-[10px] font-sans text-slate-400 normal-case tracking-normal">available players only</span></td>
              {COLS.map(([k]) => <Cell key={k} b={before.totals[k]} a={after.totals[k]} testId={`rotation-total-${k}`} />)}
            </tr>
          </tbody>
        </table>
      </div>
      {missing.length > 0 && (
        <p className="px-4 py-3 text-[11px] text-slate-500 border-t border-slate-800" data-testid="rotation-no-history">
          No {ctx.season_label} minutes yet (not projected): {missing.map((r) => r.name).join(", ")}
        </p>
      )}
    </div>
  );
}
